import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, X, Crown, Inbox } from "lucide-react";

const TYPE_LABEL = { quotation: "Harga Penawaran", invoice: "Invoice Final", escalation: "Eskalasi" };
const STATUS_CLS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };

function ApprovalCard({ a, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/approvals/${a.id}/decide`, { decision, note });
      toast.success(decision === "approved" ? "Disetujui" : "Ditolak");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-card rounded-sm p-5" data-testid={`approval-${a.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline text-muted-foreground">{TYPE_LABEL[a.type] || a.type} · {a.case_number} · kode <span className="font-mono">{a.code}</span></div>
          <div className="font-heading font-bold mt-1">{a.title}</div>
          <div className="text-xs text-muted-foreground">{a.customer} · diminta oleh {a.requested_by}</div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-sm ${STATUS_CLS[a.status]}`} data-testid={`approval-status-${a.id}`}>{a.status}</span>
      </div>
      <pre className="mt-3 text-xs bg-secondary rounded-sm p-3 whitespace-pre-wrap font-mono leading-relaxed">{a.detail}</pre>
      <div className="text-[11px] text-muted-foreground mt-2">Notifikasi: {a.notify_log || "-"}</div>
      {a.status === "pending" ? (
        <div className="mt-3 space-y-2">
          <Textarea data-testid={`approval-note-${a.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional, wajib diisi bila menolak agar agent bisa revisi)" className="rounded-sm bg-secondary min-h-[60px]" />
          <div className="flex gap-2">
            <Button disabled={busy} onClick={() => decide("approved")} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-btn-${a.id}`}><Check className="w-4 h-4 mr-1" /> Setujui</Button>
            <Button disabled={busy} variant="outline" onClick={() => decide("rejected")} className="rounded-sm text-red-600" data-testid={`reject-btn-${a.id}`}><X className="w-4 h-4 mr-1" /> Tolak</Button>
          </div>
        </div>
      ) : (
        <div className="text-xs mt-2">Diputuskan oleh {a.decided_by} · {(a.decided_at || "").slice(0, 16).replace("T", " ")}{a.note ? ` — ${a.note}` : ""}</div>
      )}
    </div>
  );
}

export default function Approvals() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/approvals${tab === "pending" ? "?status=pending" : ""}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="approvals-page">
      <PageHeader overline="Human-in-the-loop" title="Approval Direktur" />
      <div className="p-8 max-w-4xl space-y-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Crown className="w-4 h-4 text-amber-600" /> Setiap permintaan juga dikirim ke email Ongah Hermanto dengan tombol Setujui / Tolak.</div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm bg-secondary">
            <TabsTrigger value="pending" className="rounded-sm" data-testid="tab-approvals-pending">Menunggu</TabsTrigger>
            <TabsTrigger value="all" className="rounded-sm" data-testid="tab-approvals-all">Semua</TabsTrigger>
          </TabsList>
        </Tabs>
        {rows.length === 0 && <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-sm" data-testid="approvals-empty"><Inbox className="w-6 h-6 mx-auto mb-2 opacity-50" />Tidak ada approval.</div>}
        {rows.map((a) => <ApprovalCard key={a.id} a={a} onDone={load} />)}
      </div>
    </div>
  );
}
